/**
 * SOLOMON — Inbound consent funnel: public landing page (Build B frontend).
 *
 * Seller-facing, UNAUTHENTICATED. Rendered by main.jsx when pathname starts
 * with /offer, OUTSIDE the AuthProvider/SolomonProvider — no app machinery.
 * Posts to the public POST /api/funnel/opt-in. The consent checkbox text is
 * sent verbatim as `consent_text` (the TCPA evidentiary snapshot).
 *
 * First-pass design — iterate live with the operator. Placeholders flagged
 * with TODO(brand) are operator decisions (brand name, trust copy, Terms/
 * Privacy URLs). The consent wording is attorney-review-gated before go-live.
 */

import { useState, useEffect } from 'react';
import { loadAdsTag, fireConversion } from '../../lib/adsTag';

const GADS_OPTIN_SEND_TO  = import.meta.env.VITE_GADS_OPTIN_SEND_TO  || null;
const GADS_BOOKED_SEND_TO = import.meta.env.VITE_GADS_BOOKED_SEND_TO || null;
import './OfferLanding.css';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const BRAND_NAME = 'Reid Management';

// The EXACT opt-in wording shown to the seller. Sent verbatim as consent_text.
// DRAFT — attorney-review-gated before go-live (see scope doc compliance fence).
const CONSENT_TEXT =
  `By checking this box and submitting, I authorize ${BRAND_NAME} and its ` +
  `representatives to contact me at the phone number I provided — including by ` +
  `autodialed and prerecorded/artificial-voice calls and text messages — about ` +
  `a cash offer for my property and related real-estate services. I understand ` +
  `consent is not a condition of any purchase or service, that message frequency ` +
  `varies, and that message and data rates may apply. I can reply STOP to opt out ` +
  `at any time.`;

// Ad-source attribution from the landing URL (e.g. offer.solomonhq.com?src=google).
// Captured once and sent with the opt-in so Solomon can attribute ads → deals.
const ATTRIBUTION_KEYS = ['src', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid'];
function readAttribution() {
  const q = new URLSearchParams(window.location.search);
  const a = {};
  for (const k of ATTRIBUTION_KEYS) {
    const v = q.get(k);
    if (v) a[k] = v.slice(0, 200);
  }
  if (document.referrer) a.referrer = document.referrer.slice(0, 200);
  return a;
}

const TRUST = [
  { k: 'as-is',    t: 'Sell as-is',             s: 'No repairs, no cleanup.' },
  { k: 'no-fees',  t: 'No fees or commissions', s: 'What we offer is what you get.' },
  { k: 'timeline', t: 'Your timeline',          s: 'ASAP or as long as you need — we work with you.' },
];

const EMPTY = {
  name: '', phone: '',
  property_address: '', property_city: '', property_state: '', property_zip: '',
  notes: '',
  company: '', // honeypot — must stay empty
};

// Callback availability — always shown in Eastern time (the business runs ET),
// rolling forward from "now". Hours/count are easy design knobs.
const SLOT_HOURS = [10, 13, 16]; // 10am · 1pm · 4pm Eastern
const ET = 'America/New_York';
const LEAD_MS = 3600_000; // slots must be ≥1h out

// The UTC instant that is `hour`:00 Eastern on the given Y/M/D (DST-correct).
function easternInstant(year, monthIndex, day, hour) {
  const utcGuess = Date.UTC(year, monthIndex, day, hour, 0, 0);
  const p = new Intl.DateTimeFormat('en-US', {
    timeZone: ET, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(utcGuess)).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  const seenAsNY = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second);
  return new Date(utcGuess - (seenAsNY - utcGuess));
}

// Next `count` Eastern weekdays (Mon–Fri) that still have an open slot.
function buildDays(now = new Date(), count = 5) {
  const days = [];
  const seen = new Set();
  for (let i = 0; i < 16 && days.length < count; i++) {
    const probe = new Date(now.getTime() + i * 86_400_000);
    const wd = new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'short' }).format(probe);
    if (wd === 'Sat' || wd === 'Sun') continue;
    const p = new Intl.DateTimeFormat('en-US', { timeZone: ET, year: 'numeric', month: 'numeric', day: 'numeric' })
      .formatToParts(probe).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
    const key = `${p.year}-${p.month}-${p.day}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const y = +p.year, m = +p.month - 1, d = +p.day;
    const hasSlot = SLOT_HOURS.some((h) => easternInstant(y, m, d, h).getTime() > now.getTime() + LEAD_MS);
    if (!hasSlot) continue;
    days.push({
      key, year: y, month: m, day: d,
      label: new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'long', month: 'short', day: 'numeric' }).format(probe),
    });
  }
  return days;
}

// Open times for a chosen day, in Eastern, labeled "ET".
function buildTimesForDay(day, now = new Date()) {
  return SLOT_HOURS
    .map((h) => easternInstant(day.year, day.month, day.day, h))
    .filter((dt) => dt.getTime() > now.getTime() + LEAD_MS)
    .map((dt) => ({
      iso: dt.toISOString(),
      label: new Intl.DateTimeFormat('en-US', { timeZone: ET, hour: 'numeric', minute: '2-digit' }).format(dt) + ' ET',
    }));
}

export default function OfferLanding() {
  // DEV-only: /offer?preview=done renders the success/booking screen directly
  // so the booking UI can be designed without a running backend. No effect in prod.
  const PREVIEW = import.meta.env.DEV
    ? new URLSearchParams(window.location.search).get('preview')
    : null;

  const [form, setForm]       = useState(EMPTY);
  const [consent, setConsent] = useState(false);
  const [status, setStatus]   = useState(PREVIEW === 'done' ? 'done' : 'idle'); // idle | submitting | done | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [bookingToken, setBookingToken] = useState(null);
  const [bookStatus, setBookStatus] = useState('offer'); // offer | booking | booked
  const [bookedWhen, setBookedWhen] = useState(null);
  const [bookErr, setBookErr]       = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [attribution] = useState(readAttribution); // captured once on mount
  const [formStartedAt] = useState(() => Date.now()); // bot-timing signal sent on opt-in

  // Load the Google Ads tag once, only here on the public funnel. Never in the
  // authenticated app (OfferLanding only renders on the offer branch).
  useEffect(() => { loadAdsTag(); }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit =
    form.property_address.trim() &&
    form.phone.trim() &&
    consent &&
    status !== 'submitting';

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setErrorMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/funnel/opt-in`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, consent: true, consent_text: CONSENT_TEXT, attribution, form_started_at: formStartedAt }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Something went wrong.');
      setBookingToken(data.data?.bookingToken || null);
      setStatus('done');
      fireConversion(GADS_OPTIN_SEND_TO);
    } catch (err) {
      setErrorMsg(err.message || 'Could not submit. Please try again.');
      setStatus('error');
    }
  }

  async function handleBook(iso) {
    if (!bookingToken || bookStatus === 'booking') return;
    setBookStatus('booking');
    setBookErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/funnel/book`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: bookingToken, scheduledFor: iso }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        const friendly = data.error === 'slot_taken'
          ? 'That time was just taken — please pick another.'
          : (data.error || 'Could not book that time.');
        throw new Error(friendly);
      }
      setBookedWhen(data.data?.scheduledFor || iso);
      setBookStatus('booked');
      fireConversion(GADS_BOOKED_SEND_TO);
    } catch (err) {
      setBookErr(err.message || 'Could not book that time. Please try another.');
      setBookStatus('offer');
    }
  }

  return (
    <div className="offer-root">
      <div className="offer-bokeh" aria-hidden="true" />

      <header className="offer-masthead">
        <span className="offer-wordmark">{BRAND_NAME}</span>
      </header>

      <main className="offer-shell">
        {/* ── Left: the pitch ─────────────────────────────── */}
        <section className="offer-pitch">
          <p className="offer-eyebrow">Dear Homeowners</p>
          <h1 className="offer-headline">
            Get a <em className="offer-fair">fair</em> cash offer for your home.
          </h1>
          <p className="offer-sub">
            Sell as-is — no repairs, no agent fees, no obligation. Tell us about your
            property and we’ll reach out with a real number.
          </p>

          <ul className="offer-trust">
            {TRUST.map((x) => (
              <li key={x.k} className="offer-trust-item">
                <span className="offer-trust-dot" aria-hidden="true" />
                <span>
                  <strong>{x.t}</strong>
                  <em>{x.s}</em>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* ── Right: the form ─────────────────────────────── */}
        <section className="offer-card">
          {status === 'done' ? (
            <div className="offer-done">
              <div className="offer-done-mark" aria-hidden="true">✓</div>
              {bookStatus === 'booked' ? (
                <>
                  <h2>You’re booked.</h2>
                  <p>
                    We’ll call you{bookedWhen
                      ? ` ${new Intl.DateTimeFormat('en-US', { timeZone: ET, weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(new Date(bookedWhen))} ET`
                      : ' at your selected time'}. Talk soon{form.name ? `, ${form.name.split(' ')[0]}` : ''}.
                  </p>
                </>
              ) : (
                <>
                  <h2>We’ve got it{form.name ? `, ${form.name.split(' ')[0]}` : ''}.</h2>
                  {!selectedDay ? (
                    <>
                      <p>Want to lock in a time for your call? Pick a day — or we’ll just reach out shortly.</p>
                      <div className="offer-days">
                        {buildDays().map((d) => (
                          <button
                            key={d.key} type="button" className="offer-day"
                            onClick={() => { setSelectedDay(d); setBookErr(null); }}
                          >
                            {d.label}
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <button type="button" className="offer-back" onClick={() => setSelectedDay(null)}>
                        ← Change day
                      </button>
                      <p className="offer-slot-label">{selectedDay.label} — pick a time (all times ET)</p>
                      <div className="offer-slots">
                        {buildTimesForDay(selectedDay).map((s) => (
                          <button
                            key={s.iso} type="button" className="offer-slot"
                            disabled={bookStatus === 'booking'}
                            onClick={() => handleBook(s.iso)}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                  {bookErr && <div className="offer-error">{bookErr}</div>}
                  <p className="offer-fineprint">No obligation — this just reserves a callback.</p>
                </>
              )}
            </div>
          ) : (
            <form className="offer-form" onSubmit={onSubmit} noValidate>
              <h2 className="offer-card-title">Let Us Help You</h2>
              <p className="offer-pointer">
                <span className="offer-pointer-arrow" aria-hidden="true">↓</span>
                Start here — tell us about the property
              </p>

              <label className="offer-field offer-field-wide">
                <span>Property address</span>
                <input
                  type="text" autoComplete="street-address" required
                  placeholder="123 Main St"
                  value={form.property_address} onChange={set('property_address')}
                />
              </label>

              <div className="offer-row">
                <div className="offer-field offer-grow">
                  <input type="text" aria-label="City" placeholder="City"
                    value={form.property_city} onChange={set('property_city')} />
                </div>
                <div className="offer-field offer-state">
                  <input type="text" aria-label="State" placeholder="State" maxLength={2}
                    value={form.property_state} onChange={set('property_state')} />
                </div>
                <div className="offer-field offer-zip">
                  <input type="text" inputMode="numeric" aria-label="ZIP" placeholder="ZIP"
                    value={form.property_zip} onChange={set('property_zip')} />
                </div>
              </div>

              <div className="offer-row">
                <label className="offer-field offer-grow">
                  <span>Your name</span>
                  <input type="text" autoComplete="name" placeholder="Jane Smith"
                    value={form.name} onChange={set('name')} />
                </label>
                <label className="offer-field offer-grow">
                  <span>Phone</span>
                  <input type="tel" autoComplete="tel" required placeholder="(317) 555-0123"
                    value={form.phone} onChange={set('phone')} />
                </label>
              </div>

              <label className="offer-field offer-field-wide">
                <span>Anything we should know about the home? <em className="offer-opt">optional</em></span>
                <textarea
                  className="offer-textarea" rows={3}
                  placeholder="Condition, repairs needed, damage, recent updates, why you're selling — anything that helps us give you an accurate offer."
                  value={form.notes} onChange={set('notes')}
                />
              </label>

              {/* Honeypot — visually hidden, off-screen; bots fill it. */}
              <input
                className="offer-hp" tabIndex={-1} autoComplete="off"
                aria-hidden="true" value={form.company} onChange={set('company')}
                name="company" placeholder="Company"
              />

              <label className="offer-consent">
                <input type="checkbox" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)} />
                <span>{CONSENT_TEXT}</span>
              </label>

              {status === 'error' && <div className="offer-error">{errorMsg}</div>}

              <button type="submit" className="offer-submit" disabled={!canSubmit}>
                {status === 'submitting' ? 'Sending…' : 'Get My Cash Offer'}
              </button>

              <p className="offer-fineprint">
                No spam. No obligation. <a className="offer-fineprint-link" href="/privacy">Privacy Policy</a>
                {/* Terms link still omitted until the SMS channel (Telnyx/10DLC) ships. */}
              </p>
            </form>
          )}
        </section>
      </main>
    </div>
  );
}
